import React, { useState } from 'react';

import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Collapsible } from '@buildoutinc/blueprint-react/ui/Collapsible';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faChevronDown, faChevronRight, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { put } from 'helpers/api';
import { currentView, latestRecommendations } from '../../hooks/use_next_actions';
import { useConversationContext } from '../../conversation_context';
import { useNextActionsContext } from '../../next_actions_context';

import type { NextActionState } from '../../hooks/use_next_actions';
import type { Recommendation } from '../types';

interface RecommendationCarouselProps {
  recommendations: Recommendation[];
  toolCallId: number;
}

export function PinnedRecommendations() {
  const { messages } = useConversationContext();

  const latest = latestRecommendations(messages);
  if (!latest) {
    return null;
  }

  return (
    <RecommendationCarousel
      key={latest.id}
      recommendations={latest.recommendations}
      toolCallId={latest.id}
    />
  );
}

function completionFailure(recommendation: Recommendation, thrown: unknown) {
  const [reason] = Array.isArray(thrown) ? thrown : [];
  const refusal = `Couldn't complete "${recommendation.title}"`;

  return typeof reason === 'string' ? `${refusal}: ${reason}. Open it to finish.` : refusal;
}

function RecommendationCarousel({ recommendations, toolCallId }: RecommendationCarouselProps) {
  const nextActions = useNextActionsContext();
  const [completing, setCompleting] = useState(false);

  const state = nextActions.stateFor(toolCallId);
  const { dismissed, open } = state;
  const { index, recommendation, remaining } = currentView(recommendations, state);
  if (dismissed || !recommendation) {
    return null;
  }

  function update(changes: Partial<NextActionState>) {
    nextActions.update(toolCallId, changes);
  }

  async function markDone() {
    if (recommendation.completePath) {
      setCompleting(true);
      try {
        await put(recommendation.completePath, { completed: true });
      } catch (thrown) {
        showSnackbar({ text: completionFailure(recommendation, thrown), variant: 'danger' });
        return;
      } finally {
        setCompleting(false);
      }

      showSnackbar({ text: `Completed "${recommendation.title}"`, variant: 'success' });
    }

    update({ done: [...nextActions.stateFor(toolCallId).done, recommendation.position] });
  }

  return (
    <Collapsible
      className="ai-assistant-pinned flex-shrink-0 px-3 py-3"
      open={open}
      onOpenChange={next => update({ open: next })}
    >
      <Card className="bg-gradient-ai-card border-purple-heart-200 rounded-3 shadow-lg overflow-hidden">
        <Card.Body className="d-flex flex-column gap-2 bg-transparent">
          <div className="d-flex align-items-center gap-2">
            <Collapsible.Trigger
              className="btn btn-link d-flex align-items-center gap-2 p-0 text-purple-heart-700 text-decoration-none"
            >
              <FontAwesomeIcon className="fs-xs" icon={open ? faChevronDown : faChevronRight} />
              <span className="fs-sm fw-semibold text-uppercase">Next actions</span>
              <Badge className="bg-purple-heart-200" variant="secondary">
                {index + 1} of {remaining.length}
              </Badge>
            </Collapsible.Trigger>
            <div className="d-flex align-items-center gap-1 ms-auto">
              <Button
                aria-label="Previous recommendation"
                disabled={completing || !open || remaining.length < 2}
                icon={faArrowLeft}
                size="sm"
                variant="ghost"
                onClick={() => update({ index: (index - 1 + remaining.length) % remaining.length })}
              />
              <Button
                aria-label="Next recommendation"
                disabled={completing || !open || remaining.length < 2}
                icon={faArrowRight}
                size="sm"
                variant="ghost"
                onClick={() => update({ index: (index + 1) % remaining.length })}
              />
              <Button
                aria-label="Close next actions"
                icon={faXmark}
                size="sm"
                variant="ghost"
                onClick={() => update({ dismissed: true })}
              />
            </div>
          </div>
          <Collapsible.Content>
            <div className="d-flex flex-column">
              <div className="fw-semibold">{recommendation.title}</div>
              {recommendation.why.length > 0 && (
                <div className="text-muted mt-1">{recommendation.why.join('. ')}.</div>
              )}
              <div className="d-flex align-items-center gap-2 mt-3">
                <Button href={recommendation.url} label={recommendation.linkLabel} size="sm" />
                <Button
                  disabled={completing}
                  label="Done"
                  size="sm"
                  variant="ghost"
                  onClick={markDone}
                />
              </div>
            </div>
          </Collapsible.Content>
        </Card.Body>
      </Card>
    </Collapsible>
  );
}
